import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin, ShieldAlert, Ticket } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, fmtDate } from "@/lib/api";
import { Navbar } from "@/components/landing/Hero";

const WAITING = ["pending_payment", "pending_verification"];

export default function EventTicket() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const code = params.get("code");
  const [data, setData] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        let status;
        if (orderId) {
          const { data: d } = await api.get(`/public/ticketing/registration/${orderId}`);
          setData(d);
          status = d.status;
        } else if (code) {
          const { data: d } = await api.get(`/public/ticketing/ticket/${code}`);
          setData({ ...d, event_title: d.event?.title });
          status = d.status;
        }
        if (!WAITING.includes(status) && pollRef.current) clearInterval(pollRef.current);
      } catch {
        setData(false);
        if (pollRef.current) clearInterval(pollRef.current);
      }
    };
    load();
    pollRef.current = setInterval(load, 5000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [orderId, code]);

  if (data === false) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="event-ticket-notfound">
        <Navbar forceGlass />
        <div className="pt-40 pb-20 text-center px-6">
          <p className="font-black text-xl text-slate-900">Tiket tidak ditemukan</p>
          <Link to={`/event/${id}`} className="inline-block mt-5 font-bold text-indigo-600">← Kembali ke event</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const pendingPayment = data.status === "pending_payment";
  const pendingVerification = data.status === "pending_verification";
  const rejected = data.status === "rejected";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-ticket-page">
      <Navbar forceGlass />
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          {pendingPayment ? (
            <div className="bg-white rounded-3xl p-10 text-center border border-slate-100 shadow-sm" data-testid="event-ticket-pending">
              <Clock className="w-10 h-10 mx-auto text-amber-500 animate-pulse" />
              <p className="mt-4 font-black text-lg text-slate-900">Menunggu Pembayaran…</p>
              <p className="mt-1 text-sm text-slate-500">Halaman ini akan otomatis memperbarui begitu pembayaran Anda berhasil.</p>
            </div>
          ) : pendingVerification ? (
            <div className="bg-white rounded-3xl p-10 text-center border border-slate-100 shadow-sm" data-testid="event-ticket-pending-verification">
              <Ticket className="w-10 h-10 mx-auto text-amber-500 animate-pulse" />
              <p className="mt-4 font-black text-lg text-slate-900">Menunggu Verifikasi Panitia…</p>
              <p className="mt-1 text-sm text-slate-500">Bukti pembayaran Anda sedang diperiksa. Kode tiket: <b>{data.ticket_code}</b>. Halaman ini akan otomatis memperbarui begitu tiket diverifikasi.</p>
            </div>
          ) : rejected ? (
            <div className="bg-white rounded-3xl p-10 text-center border border-red-100 shadow-sm" data-testid="event-ticket-rejected">
              <ShieldAlert className="w-10 h-10 mx-auto text-red-500" />
              <p className="mt-4 font-black text-lg text-slate-900">Pembayaran Belum Terverifikasi</p>
              <p className="mt-1 text-sm text-slate-500">Mohon maaf, bukti pembayaran Anda belum dapat kami verifikasi. Silakan hubungi panitia.{data.admin_note ? ` Catatan: ${data.admin_note}` : ""}</p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 text-center border border-slate-100 shadow-sm" data-testid="event-ticket-valid">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1"><CheckCircle2 className="w-3.5 h-3.5" />Tiket Aktif</span>
              <p className="mt-4 font-black text-xl text-slate-900" data-testid="event-ticket-event-title">{data.event_title}</p>
              {data.event && (
                <div className="mt-2 flex flex-col items-center gap-1 text-xs text-slate-500 font-semibold">
                  <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(data.event.start_date, { day: "numeric", month: "long", year: "numeric" })} · {data.event.time}</span>
                  {data.event.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{data.event.location}</span>}
                </div>
              )}
              <p className="mt-1 text-sm text-slate-500">{data.name}{data.ticket_type ? ` · ${data.ticket_type}` : ""}</p>
              <div className="mt-6 bg-slate-50 rounded-2xl p-6 inline-block">
                <QRCodeSVG value={`GIM-TIX:${data.ticket_code}`} size={180} data-testid="event-ticket-qr" />
              </div>
              <p className="mt-4 font-black tracking-widest text-slate-900" data-testid="event-ticket-code">{data.ticket_code}</p>
              <p className="mt-1 text-[11px] text-slate-400">Tunjukkan QR ini di pintu masuk untuk dipindai panitia</p>
              <p className="mt-4 text-[11px] text-slate-400 bg-slate-50 rounded-xl p-3">Kode ini juga telah dikirim ke email Anda sebagai cadangan. Kode checkpoint tambahan (bila berlaku) akan dikirim otomatis ke email setelah E-Tiket Anda dipindai di pintu masuk.</p>
            </div>
          )}
          <div className="mt-6 text-center">
            <Link to="/event" data-testid="event-ticket-back" className="text-sm font-bold text-slate-500 hover:text-slate-900 transition">← Lihat Event Lain</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
